import { Timestamp, collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { db } from "@/firebase/app";
import type { OverlayLowerThirdPerson } from "@/models/overlayConfig";
import { apiRequest } from "./functionsClient";

export const LEAGUE_ATHLETES_SOURCE_ID = "league-athletes";

/** Backend workspace slot identifier: "lowerThird:{instanceId}", "playerCard:{instanceId}", "pylon:{instanceId}", "gpsPylon:{instanceId}", "sortFilterPylon:{instanceId}", "podium:{instanceId}", or "startList:{instanceId}". */
export type OverlayPersonWorkspaceSlot = string;

export function lowerThirdWorkspaceSlot(instanceId: string): OverlayPersonWorkspaceSlot {
  return `lowerThird:${instanceId}`;
}

export function playerCardWorkspaceSlot(instanceId: string): OverlayPersonWorkspaceSlot {
  return `playerCard:${instanceId}`;
}

export function pylonWorkspaceSlot(instanceId: string): OverlayPersonWorkspaceSlot {
  return `pylon:${instanceId}`;
}

export function gpsPylonWorkspaceSlot(instanceId: string): OverlayPersonWorkspaceSlot {
  return `gpsPylon:${instanceId}`;
}

export function sortFilterPylonWorkspaceSlot(instanceId: string): OverlayPersonWorkspaceSlot {
  return `sortFilterPylon:${instanceId}`;
}

export function podiumWorkspaceSlot(instanceId: string): OverlayPersonWorkspaceSlot {
  return `podium:${instanceId}`;
}

export function startListWorkspaceSlot(instanceId: string): OverlayPersonWorkspaceSlot {
  return `startList:${instanceId}`;
}

export interface OverlayPersonSourceCache {
  sourceID: string;
  status: "loading" | "ready" | "error";
  activeGenerationID?: string;
  recordCount?: number;
  error?: string;
  loadedAt?: Date;
  expiresAt?: Date;
}

type WorkspaceRecord = { index: number; record: Record<string, unknown> };

function parseSourceCache(id: string, data: Record<string, unknown>): OverlayPersonSourceCache | null {
  const status = data.status;
  if (status !== "loading" && status !== "ready" && status !== "error") return null;
  return {
    sourceID: id,
    status,
    activeGenerationID: typeof data.activeGenerationID === "string" ? data.activeGenerationID : undefined,
    recordCount: typeof data.recordCount === "number" ? data.recordCount : undefined,
    error: typeof data.error === "string" ? data.error : undefined,
    // Absent while the doc is in its first "loading" state.
    loadedAt: data.loadedAt instanceof Timestamp ? data.loadedAt.toDate() : undefined,
    expiresAt: data.expiresAt instanceof Timestamp ? data.expiresAt.toDate() : undefined,
  };
}

/** Live, manager-only source caches for the open overlay. */
export function useOverlayPersonWorkspace(overlayId: string | undefined, enabled = true) {
  const [sources, setSources] = useState<Record<string, OverlayPersonSourceCache>>({});
  const [recordsBySource, setRecordsBySource] = useState<Record<string, WorkspaceRecord[]>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!overlayId || !enabled) {
      setSources({});
      setRecordsBySource({});
      return;
    }
    return onSnapshot(
      collection(db, "overlayPersonWorkspaces", overlayId, "sourceCaches"),
      (snapshot) => {
        const next = Object.fromEntries(snapshot.docs
          .map((entry) => parseSourceCache(entry.id, entry.data()))
          .filter((entry): entry is OverlayPersonSourceCache => entry !== null)
          .map((entry) => [entry.sourceID, entry]));
        setSources(next);
        setError(null);
      },
      (snapshotError) => setError(snapshotError.message),
    );
  }, [overlayId, enabled]);

  const sourceSignature = Object.values(sources)
    .filter((source) => source.status === "ready" && source.activeGenerationID)
    .map((source) => `${source.sourceID}:${source.activeGenerationID}`)
    .sort()
    .join("|");

  useEffect(() => {
    if (!overlayId || !enabled) return;
    const readySources = Object.values(sources).filter((source) => source.status === "ready" && source.activeGenerationID);
    const unsubscribers = readySources.map((source) => onSnapshot(
      query(
        collection(db, "overlayPersonWorkspaces", overlayId, "sourceCaches", source.sourceID, "generations", source.activeGenerationID!, "records"),
        orderBy("index"),
      ),
      (snapshot) => {
        const records = snapshot.docs.flatMap((entry) => {
          const json = entry.data().json;
          const index = entry.data().index;
          if (typeof json !== "string" || typeof index !== "number") return [];
          try {
            const record = JSON.parse(json) as Record<string, unknown>;
            return record && typeof record === "object" && !Array.isArray(record) ? [{ index, record }] : [];
          } catch {
            return [];
          }
        });
        setRecordsBySource((previous) => ({ ...previous, [source.sourceID]: records }));
      },
      (snapshotError) => setError(snapshotError.message),
    ));
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      setRecordsBySource((previous) => {
        const next = { ...previous };
        for (const source of readySources) delete next[source.sourceID];
        return next;
      });
    };
  }, [overlayId, enabled, sourceSignature]);

  return useMemo(() => ({ sources, recordsBySource, error }), [sources, recordsBySource, error]);
}

export async function selectOverlayPersonSource(
  overlayId: string,
  slot: OverlayPersonWorkspaceSlot,
  sourceID: string | undefined,
): Promise<void> {
  await apiRequest(`/overlays/${encodeURIComponent(overlayId)}/person-sources/select-source`, {
    method: "POST",
    body: { slot, sourceID },
  });
}

export async function loadOverlayPersonSource(
  overlayId: string,
  slot: OverlayPersonWorkspaceSlot,
  sourceID: string,
  options: { reload?: boolean; googleAccessToken?: string } = {},
): Promise<void> {
  await apiRequest(`/overlays/${encodeURIComponent(overlayId)}/person-sources/${encodeURIComponent(sourceID)}/load`, {
    method: "POST",
    body: { slot, ...options },
  });
}

export async function selectOverlayPerson(
  overlayId: string,
  slot: OverlayPersonWorkspaceSlot,
  sourceID: string,
  recordKey: string,
  person: OverlayLowerThirdPerson,
): Promise<void> {
  await apiRequest(`/overlays/${encodeURIComponent(overlayId)}/person-sources/select-person`, {
    method: "POST",
    body: { slot, sourceID, recordKey, person },
  });
}
